import type React from 'react';

import {
  CalendarMonthRounded,
  CallMadeRounded,
  ChevronRightRounded,
  CircleRounded,
  DriveEtaRounded,
  ExpandMoreRounded,
  GraphicEqRounded,
  HeadsetMicRounded,
  PeopleAltRounded,
  QueueRounded,
  ReportProblemRounded,
} from '@mui/icons-material';
import { Alert, Box, Chip, LinearProgress, Stack, Typography } from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { useTranslate } from 'react-admin';

import { isoToTime } from 'src/lib/timeUtils';

import {
  buildChannelSummary,
  buildDetailEntries,
  filterDuplicateEntries,
  getEventTypeLabel,
  getFakeVoiceDetectionSummary,
  isSignificantEvent,
  useCallSessionEvents,
  useChannelTree,
  type ChannelTreeNode,
} from '../utils/callSessionEventsUtils';

import type { CallSessionEvent } from '@ragu-components/ord-types';

const sanitizeId = (value: string): string => value.replace(/[^a-z0-9_-]/gi, '_');

/**
 * Returns the most relevant event for a channel used to decorate the tree label.
 */
const getChannelHeadlineEvent = (channel: ChannelTreeNode): CallSessionEvent | null => {
  const significantEvents = channel.events.filter(isSignificantEvent);
  return significantEvents[significantEvents.length - 1] ?? null;
};

/**
 * Maps event types to representative icons for quick scanning.
 * Icons match those used in Menu.tsx for consistency.
 */
const getEventTypeIcon = (event: CallSessionEvent): React.ReactElement => {
  const type = event.details?.type;
  switch (type) {
    case 'agent_call':
      return <HeadsetMicRounded fontSize='small' />;
    case 'user_call':
      return <PeopleAltRounded fontSize='small' />;
    case 'outbound_call':
      return <CallMadeRounded fontSize='small' />;
    case 'queue':
      return <QueueRounded fontSize='small' />;
    case 'call_me':
      return <DriveEtaRounded fontSize='small' />;
    case 'calendar':
      return <CalendarMonthRounded fontSize='small' />;
    case 'ai_voice_analysis':
    case 'fake_voice_detection':
      return <GraphicEqRounded fontSize='small' />;
    case 'service_failure':
      return <ReportProblemRounded fontSize='small' />;
    case 'presence_router':
    case 'disconnect':
    default:
      return <CircleRounded fontSize='small' />;
  }
};

/**
 * Represents either an event or a child channel for chronological rendering.
 */
type TreeItemElement = { type: 'event'; event: CallSessionEvent } | { type: 'channel'; channel: ChannelTreeNode };

/**
 * Gets the timestamp for sorting tree elements chronologically.
 */
const getTreeItemTimestamp = (item: TreeItemElement): number => {
  if (item.type === 'event') {
    return new Date(item.event.ts_event).getTime();
  }
  const firstEvent = item.channel.events[0];
  return firstEvent !== undefined ? new Date(firstEvent.ts_event).getTime() : Number.POSITIVE_INFINITY;
};

/**
 * Renders a channel node inside the tree, including summary chips and child events.
 * Events and child channels are interleaved chronologically.
 * If isRoot is true, renders children without a wrapping TreeItem.
 */
const ChannelTreeItem = ({
  node,
  isRoot = false,
  showAdvanced = false,
}: {
  node: ChannelTreeNode;
  isRoot?: boolean;
  showAdvanced?: boolean;
}): React.ReactElement => {
  const translate = useTranslate();
  const summary = buildChannelSummary(node, translate);
  const headlineEvent = getChannelHeadlineEvent(node);
  const fallbackEvent = headlineEvent ?? node.events[node.events.length - 1] ?? null;
  const headlineLabel = fallbackEvent !== null ? getEventTypeLabel(fallbackEvent, translate) : null;
  const primaryDetail = headlineLabel?.detail ?? null;
  const channelNodeId = `channel-${sanitizeId(node.channelId)}`;
  const timelineTokens: string[] = [summary.eventCountLabel];

  // Build time range: (start - end) or just (time) if same
  if (summary.firstEventTime !== undefined && summary.lastEventTime !== undefined) {
    if (summary.firstEventTime === summary.lastEventTime) {
      // Same time, show once
      timelineTokens.push(`(${summary.firstEventTime})`);
    } else {
      // Show range if different times
      timelineTokens.push(`(${summary.firstEventTime} - ${summary.lastEventTime})`);
    }
  } else if (summary.firstEventTime !== undefined) {
    // Show single time
    timelineTokens.push(`(${summary.firstEventTime})`);
  }

  const hasAnswerTime =
    fallbackEvent?.details !== undefined &&
    'answer_time' in fallbackEvent?.details &&
    fallbackEvent?.details?.answer_time !== null &&
    fallbackEvent?.details?.answer_time !== undefined;

  const chipColor = hasAnswerTime ? 'success' : headlineEvent !== null ? 'primary' : 'default';

  const channelLabel = (
    <Stack direction='row' spacing={1} alignItems='center' sx={{ minWidth: 0 }}>
      {fallbackEvent !== null && headlineLabel !== null && (
        <Chip
          icon={getEventTypeIcon(fallbackEvent)}
          label={headlineLabel.label}
          size='small'
          color={chipColor}
          variant='outlined'
          sx={{ maxWidth: 200, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
        />
      )}
      <Box
        sx={{ minWidth: 0, '& .MuiTypography-root': { lineHeight: 1 } }}
        gap={0.5}
        display='flex'
        alignItems='baseline'
      >
        <Typography
          variant='body2'
          sx={{ fontWeight: 600, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}
        >
          {primaryDetail !== null && primaryDetail !== '' ? primaryDetail : (headlineLabel?.label ?? summary.title)}
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          {timelineTokens.join(' • ')}
        </Typography>
        {showAdvanced && (
          <>
            <Typography variant='caption' color='text.secondary'>
              {translate('resources.call_session_events.summary.parent', { _: 'Parent channel' })}:{' '}
              {summary.parentLabel}
            </Typography>
            <Typography variant='caption' color='text.disabled'>
              {translate('resources.call_session_events.fields.channel_id')}: {summary.channelId}
            </Typography>
          </>
        )}
      </Box>
    </Stack>
  );

  // Merge events and child channels, then sort chronologically.
  // When this channel renders as a wrapper (isRoot=false), the fallbackEvent is already
  // represented by the wrapper label, so exclude it from the inner elements.
  const elements: TreeItemElement[] = [
    ...node.events
      .filter((event) => isRoot || event.id !== fallbackEvent?.id)
      .map((event): TreeItemElement => ({ type: 'event', event })),
    ...node.children.map((channel): TreeItemElement => ({ type: 'channel', channel })),
  ];
  elements.sort((a, b) => getTreeItemTimestamp(a) - getTreeItemTimestamp(b));

  const renderElements = (): React.ReactNode => {
    return elements.map((element) => {
      if (element.type === 'event') {
        const event = element.event;
        const { label, detail } = getEventTypeLabel(event, translate);
        const entries = buildDetailEntries(event, translate);
        const filteredEntries = filterDuplicateEntries(entries, detail);
        const isSignificant = isSignificantEvent(event);
        const eventTime = isoToTime(event.ts_event);
        // Fake voice verdicts replace the generic "label • field: value" rendering with a
        // coloured sentence, since the raw prediction/score pair reads poorly on its own.
        const fakeVoice = getFakeVoiceDetectionSummary(event, translate);

        // CheckRounded if the event timestamp is already shown in any of the detail values
        const timeAlreadyShown = filteredEntries.some((entry) => entry.value === eventTime);
        const metaSegments: string[] = [];

        if (fakeVoice === null) {
          if (detail !== null && detail.trim() !== '') {
            metaSegments.push(detail);
          }

          metaSegments.push(...filteredEntries.map((entry) => `${entry.label}: ${entry.value}`));
        }

        if (!timeAlreadyShown) {
          metaSegments.push(`(${eventTime})`);
        }

        return (
          <TreeItem
            key={`event-${event.id}`}
            itemId={`event-${event.id}`}
            label={
              <Stack direction='row' spacing={1} alignItems='center'>
                {fakeVoice !== null ? (
                  <GraphicEqRounded fontSize='small' color={fakeVoice.color} />
                ) : (
                  getEventTypeIcon(event)
                )}
                <Box
                  sx={{ minWidth: 0, '& .MuiTypography-root': { lineHeight: 1 } }}
                  gap={0.5}
                  display='flex'
                  alignItems='baseline'
                >
                  <Typography
                    variant='body2'
                    color={fakeVoice !== null ? `${fakeVoice.color}.main` : undefined}
                    sx={{
                      fontWeight: fakeVoice !== null || isSignificant ? 600 : 400,
                    }}
                  >
                    {fakeVoice !== null
                      ? [fakeVoice.label, fakeVoice.score !== null ? `(${fakeVoice.score})` : null]
                          .filter((segment) => segment !== null)
                          .join(' ')
                      : label}
                  </Typography>
                  {metaSegments.length > 0 && (
                    <Typography variant='caption' color='text.secondary'>
                      {metaSegments.join(' • ')}
                    </Typography>
                  )}
                </Box>
              </Stack>
            }
            sx={{ '& .MuiTreeItem-content': { py: 0.35, cursor: 'default' } }}
          />
        );
      }
      return (
        <ChannelTreeItem
          key={element.channel.channelId}
          node={element.channel}
          isRoot={element.channel.children.length === 0}
          showAdvanced={showAdvanced}
        />
      );
    });
  };

  // Root channels render their children directly without a wrapper
  if (isRoot) {
    return <>{renderElements()}</>;
  }

  // Non-root channels are wrapped in a collapsible TreeItem
  return (
    <TreeItem itemId={channelNodeId} label={channelLabel} sx={{ '& .MuiTreeItem-content': { py: 0.5 } }}>
      {renderElements()}
    </TreeItem>
  );
};

interface CallSessionEventsTreeViewProps {
  showAdvanced: boolean;
}

/**
 * Tree alternative to the hierarchy cards, collapsed by default for readability.
 */
export const CallSessionEventsTreeView = ({ showAdvanced }: CallSessionEventsTreeViewProps): React.ReactElement => {
  const translate = useTranslate();
  const { events, isLoading, error } = useCallSessionEvents();
  const channelTree = useChannelTree(events);

  const defaultExpanded: string[] = [];

  if (isLoading) {
    return (
      <Box sx={{ width: '100%', py: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (error != null) {
    return (
      <Alert severity='error'>
        {translate('resources.call_session_events.errors.loadFailed', { _: 'Unable to load call session events.' })}
      </Alert>
    );
  }

  if (events.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography variant='subtitle1' gutterBottom>
          {translate('resources.call_session_events.empty.title', { _: 'No events yet' })}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          {translate('resources.call_session_events.empty.description', {
            _: 'Call session activity will appear here once events are recorded.',
          })}
        </Typography>
      </Box>
    );
  }

  return (
    <SimpleTreeView
      aria-label={translate('resources.call_session_events.name')}
      defaultExpandedItems={defaultExpanded}
      slots={{
        collapseIcon: ExpandMoreRounded,
        expandIcon: ChevronRightRounded,
      }}
      sx={{
        '& .MuiTreeItem-content': { userSelect: 'text' },
      }}
    >
      {channelTree.map((channel) => (
        <ChannelTreeItem
          key={channel.channelId}
          node={channel}
          isRoot={channel.events.length === 0 || channel.children.length === 0}
          showAdvanced={showAdvanced}
        />
      ))}
    </SimpleTreeView>
  );
};

export default CallSessionEventsTreeView;
